import type {
  CapturePolicy,
  CaptureStage,
  DocumentProfile,
  LivenessChallenge,
  StageEventRequest,
  UploadInitRequest,
  UploadInitResponse,
} from '@kyc/shared';
import { API_BASE } from '../config.js';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface StageView {
  stage: CaptureStage;
  status: string;
  attempts: number;
  rejectReasons: string[];
}

export interface ClaimResult {
  captureToken: string;
  sessionId: string;
  assuranceLevel: string;
  serverNonce: string;
  documentProfile: DocumentProfile;
  capturePolicy: CapturePolicy;
  livenessChallenge: LivenessChallenge;
  stages: StageView[];
  nextStage: CaptureStage | null;
  expiresAt: string;
}

export interface CaptureState {
  sessionId: string;
  status: string;
  stages: StageView[];
  nextStage: CaptureStage | null;
  expiresAt: string;
}

const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    return new ApiError(res.status, body.error?.code ?? 'HTTP_ERROR', body.error?.message ?? res.statusText, body.error?.details);
  } catch {
    return new ApiError(res.status, 'HTTP_ERROR', res.statusText);
  }
}

/** fetch with §21 exponential backoff for transient failures (network / 5xx). */
export async function fetchWithRetry(
  input: string,
  init: RequestInit,
  opts: { attempts?: number; baseDelayMs?: number } = {},
): Promise<Response> {
  const attempts = opts.attempts ?? 4;
  const base = opts.baseDelayMs ?? 500;
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(input, init);
      if (RETRYABLE_STATUS.has(res.status) && i < attempts - 1) {
        await new Promise((r) => setTimeout(r, base * 2 ** i));
        continue;
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, base * 2 ** i));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Network failure');
}

export class ApiClient {
  private captureToken: string | null = null;
  private claimToken: string | null = null;
  private device: Record<string, string> = {};

  constructor(private baseUrl: string = API_BASE) {}

  private async rawJson<T>(
    path: string,
    init: RequestInit,
    auth: string | null,
    attempts?: number,
  ): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json', ...(init.headers as Record<string, string> | undefined) };
    if (auth) headers.authorization = `Bearer ${auth}`;
    const res = await fetchWithRetry(`${this.baseUrl}${path}`, { ...init, headers }, { attempts });
    if (!res.ok) throw await parseError(res);
    return (await res.json()) as T;
  }

  /**
   * Capture-token call with §25 recovery: the capture token is capped at 30 minutes
   * server-side, so on expiry we transparently re-claim (allowed for every non-terminal
   * session status) and retry once.
   */
  private async json<T>(path: string, init: RequestInit = {}, attempts?: number): Promise<T> {
    try {
      return await this.rawJson<T>(path, init, this.captureToken, attempts);
    } catch (err) {
      const expired =
        err instanceof ApiError &&
        (err.code === 'TOKEN_INVALID' || err.code === 'TOKEN_MISSING' || err.status === 401);
      if (!expired || !this.claimToken) throw err;
      await this.claim(this.claimToken, this.device);
      return this.rawJson<T>(path, init, this.captureToken, attempts);
    }
  }

  async claim(claimToken: string, device: Record<string, string>): Promise<ClaimResult> {
    const result = await this.rawJson<ClaimResult>(
      '/capture/claim',
      { method: 'POST', body: JSON.stringify({ claimToken, device }) },
      null,
    );
    this.captureToken = result.captureToken;
    this.claimToken = claimToken;
    this.device = device;
    return result;
  }

  getState(): Promise<CaptureState> {
    return this.json<CaptureState>('/capture/session');
  }

  postStageEvent(stage: CaptureStage, event: StageEventRequest): Promise<{ stage: StageView; nextStage: CaptureStage | null }> {
    return this.json(`/capture/stages/${stage}/events`, { method: 'POST', body: JSON.stringify(event) });
  }

  /** Single attempt: a blind replay of init would allocate a duplicate server attempt. */
  initUpload(stage: CaptureStage, req: UploadInitRequest): Promise<UploadInitResponse> {
    return this.json(`/capture/stages/${stage}/uploads`, { method: 'POST', body: JSON.stringify(req) }, 1);
  }

  reissueUploadToken(mediaId: string): Promise<UploadInitResponse> {
    return this.json(`/capture/uploads/${mediaId}/token`, { method: 'POST', body: JSON.stringify({}) });
  }

  async putChunk(mediaId: string, index: number, chunk: ArrayBuffer, uploadToken: string): Promise<void> {
    const res = await fetchWithRetry(`${this.baseUrl}/capture/uploads/${mediaId}/chunks/${index}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/octet-stream', authorization: `Bearer ${uploadToken}` },
      body: chunk,
    });
    if (!res.ok) throw await parseError(res);
  }

  async completeUpload(mediaId: string, sha256: string, totalChunks: number, uploadToken: string): Promise<void> {
    const res = await fetchWithRetry(`${this.baseUrl}/capture/uploads/${mediaId}/complete`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${uploadToken}` },
      body: JSON.stringify({ sha256, totalChunks }),
    });
    if (!res.ok) throw await parseError(res);
  }
}
